// Lab quality control: Westgard rules, the SD index and QC holds
// (docs/plans/phase3-lab-qc-and-microbiology.md §4; ADR 0039).
//
// Every number is a scaled integer at QC_SCALE_DECIMALS, compared as a BigInt, so a rule
// never turns on floating-point rounding. The mean and SD are what the lab entered for the
// lot; nothing here computes a target. Which rules apply, and how the boundaries read, are
// provisional (plan Q1–Q3, TODO(clinical-verify)).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import { parseLabNumeric, type LabNumericValue, type LabValueErrorCode } from './values.ts';

/** QC values, means and SDs are compared at this many decimals (the most an analyte takes). */
export const QC_SCALE_DECIMALS = 4;

export const WESTGARD_RULES = ['1-2s', '1-3s', '2-2s', 'R-4s', '4-1s', '10x'] as const;
export type WestgardRule = (typeof WESTGARD_RULES)[number];

/** 1-2s is a warning; the others reject the run. */
export const WESTGARD_REJECTION_RULES: readonly WestgardRule[] = [
  '1-3s',
  '2-2s',
  'R-4s',
  '4-1s',
  '10x',
];

/**
 * The rules on when a facility hasn't chosen any for an analyte.
 * TODO(clinical-verify): plan Q1, all six until the lab lead decides.
 */
export const DEFAULT_WESTGARD_RULES: readonly WestgardRule[] = WESTGARD_RULES;

/** In words, for screens and the audit. */
export const WESTGARD_RULE_WORDS: Record<WestgardRule, string> = {
  '1-2s': 'One run more than 2 SD from the mean (warning)',
  '1-3s': 'One run more than 3 SD from the mean',
  '2-2s': 'Two runs in a row more than 2 SD from the mean, same side',
  'R-4s': 'Two runs in a row, one more than 2 SD above and one more than 2 SD below',
  '4-1s': 'Four runs in a row more than 1 SD from the mean, same side',
  '10x': 'Ten runs in a row on the same side of the mean',
};

export const QC_OUTCOMES = ['accepted', 'warning', 'rejected'] as const;
export type QcOutcome = (typeof QC_OUTCOMES)[number];

/** One run as judged: its value and the mean and SD of the target it was judged against. */
export interface QcPoint {
  readonly value: bigint;
  readonly mean: bigint;
  readonly sd: bigint;
}

export interface WestgardResult {
  readonly outcome: QcOutcome;
  /** In the order of WESTGARD_RULES. */
  readonly violations: readonly WestgardRule[];
}

/** 1 when more than k SD above the mean, -1 when more than k SD below, else 0. Strict. */
function beyond(point: QcPoint, k: bigint): -1 | 0 | 1 {
  const deviation = point.value - point.mean;
  const limit = k * point.sd;
  if (deviation > limit) return 1;
  if (-deviation > limit) return -1;
  return 0;
}

/** 1 above the mean, -1 below, 0 on it. */
function sideOfMean(point: QcPoint): -1 | 0 | 1 {
  if (point.value > point.mean) return 1;
  if (point.value < point.mean) return -1;
  return 0;
}

/** The last n points, newest last, or null when the series is shorter. */
function lastPoints(series: readonly QcPoint[], n: number): readonly QcPoint[] | null {
  return series.length < n ? null : series.slice(series.length - n);
}

function sameSideBeyond(points: readonly QcPoint[] | null, k: bigint): boolean {
  if (points === null) return false;
  const sides = points.map((p) => beyond(p, k));
  const first = sides[0] ?? 0;
  return first !== 0 && sides.every((s) => s === first);
}

const CHECKS: Record<WestgardRule, (series: readonly QcPoint[]) => boolean> = {
  '1-2s': (s) => sameSideBeyond(lastPoints(s, 1), 2n),
  '1-3s': (s) => sameSideBeyond(lastPoints(s, 1), 3n),
  '2-2s': (s) => sameSideBeyond(lastPoints(s, 2), 2n),
  'R-4s': (s) => {
    const pair = lastPoints(s, 2);
    if (pair === null) return false;
    const [a, b] = pair.map((p) => beyond(p, 2n));
    return (a === 1 && b === -1) || (a === -1 && b === 1);
  },
  '4-1s': (s) => sameSideBeyond(lastPoints(s, 4), 1n),
  '10x': (s) => {
    const ten = lastPoints(s, 10);
    if (ten === null) return false;
    const sides = ten.map(sideOfMean);
    const first = sides[0] ?? 0;
    return first !== 0 && sides.every((x) => x === first);
  },
};

function assertPoint(point: QcPoint): void {
  if (point.sd <= 0n) throw new RangeError('A QC target needs an SD above zero');
}

/**
 * Judges the newest run of a series (oldest first, newest last; runs entered in error left
 * out) against the active rules. Every window ends at the newest run, so an old violation
 * never rejects a new run by itself. Rejection rules are checked whether or not 1-2s fired
 * (plan Q3).
 */
export function evaluateWestgard(
  series: readonly QcPoint[],
  activeRules: readonly WestgardRule[],
): WestgardResult {
  if (series.length === 0) throw new RangeError('A QC evaluation needs the run being judged');
  for (const point of series) assertPoint(point);
  const active = new Set(activeRules);
  const violations = WESTGARD_RULES.filter((rule) => active.has(rule) && CHECKS[rule](series));
  const rejected = violations.some((rule) => WESTGARD_REJECTION_RULES.includes(rule));
  return {
    outcome: rejected ? 'rejected' : violations.includes('1-2s') ? 'warning' : 'accepted',
    violations,
  };
}

/** Rescales a parsed lab value to QC_SCALE_DECIMALS as a BigInt. */
export function toQcScale(value: LabNumericValue): bigint {
  if (value.decimals > QC_SCALE_DECIMALS) throw new RangeError('Too many decimals for QC');
  return BigInt(value.scaled) * 10n ** BigInt(QC_SCALE_DECIMALS - value.decimals);
}

/** A QC scaled integer from a stored `numeric` string such as "4.2500" or "-1.5". */
export function qcScaledFromDecimal(text: string): bigint {
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = text.replace(/^-/, '').split('.');
  if (/[1-9]/.test(fraction.slice(QC_SCALE_DECIMALS))) {
    throw new Error('a stored QC value has more decimals than QC keeps');
  }
  const magnitude = BigInt(
    whole + fraction.padEnd(QC_SCALE_DECIMALS, '0').slice(0, QC_SCALE_DECIMALS),
  );
  return negative ? -magnitude : magnitude;
}

/** A QC scaled integer as a decimal string for a `numeric` column: 42500n → "4.2500". */
export function qcDecimalString(scaled: bigint): string {
  const sign = scaled < 0n ? '-' : '';
  const digits = (scaled < 0n ? -scaled : scaled).toString().padStart(QC_SCALE_DECIMALS + 1, '0');
  return `${sign}${digits.slice(0, -QC_SCALE_DECIMALS)}.${digits.slice(-QC_SCALE_DECIMALS)}`;
}

/** A QC scaled integer shown with a given number of decimals (rounded half away from zero). */
export function formatQcValue(scaled: bigint, decimals: number): string {
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > QC_SCALE_DECIMALS) {
    throw new RangeError('QC display decimals must be 0 to 4');
  }
  const divisor = 10n ** BigInt(QC_SCALE_DECIMALS - decimals);
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const rounded = (magnitude + divisor / 2n) / divisor;
  const digits = rounded.toString().padStart(decimals + 1, '0');
  const text = decimals === 0 ? digits : `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}`;
  return negative && rounded !== 0n ? `-${text}` : text;
}

export type QcInputErrorCode =
  LabValueErrorCode | 'qc.comparator_not_allowed' | 'qc.sd_not_positive' | 'qc.mean_negative';

export const QC_INPUT_ERRORS: Record<
  'qc.comparator_not_allowed' | 'qc.sd_not_positive' | 'qc.mean_negative',
  string
> = {
  'qc.comparator_not_allowed': 'Enter the measured number, without < or >',
  'qc.sd_not_positive': 'The SD must be more than zero',
  'qc.mean_negative': 'This analyte cannot be negative, so neither can its mean',
};

/** A QC run's value, with the analyte's own decimals and sign rule. */
export function parseQcValue(
  text: string,
  analyte: { decimals: number; allowsNegative: boolean },
): Result<bigint, QcInputErrorCode> {
  const parsed = parseLabNumeric(text, analyte);
  if (!parsed.ok) return parsed;
  if (parsed.value.comparator !== null) return err('qc.comparator_not_allowed');
  return ok(toQcScale(parsed.value));
}

/** A target's mean and SD as the lab entered them: up to 4 decimals, SD above zero. */
export function parseQcTarget(
  input: { mean: string; sd: string },
  analyte: { allowsNegative: boolean },
): Result<{ mean: bigint; sd: bigint }, QcInputErrorCode> {
  const mean = parseLabNumeric(input.mean, {
    decimals: QC_SCALE_DECIMALS,
    allowsNegative: true,
  });
  if (!mean.ok) return mean;
  if (mean.value.comparator !== null) return err('qc.comparator_not_allowed');
  if (mean.value.scaled < 0 && !analyte.allowsNegative) return err('qc.mean_negative');
  const sd = parseLabNumeric(input.sd, { decimals: QC_SCALE_DECIMALS, allowsNegative: false });
  if (!sd.ok) return sd;
  if (sd.value.comparator !== null) return err('qc.comparator_not_allowed');
  if (sd.value.scaled <= 0) return err('qc.sd_not_positive');
  return ok({ mean: toQcScale(mean.value), sd: toQcScale(sd.value) });
}

/**
 * How many SDs the value is from the mean, in hundredths, rounded half away from zero:
 * value 4.5, mean 4.0, SD 0.2 → 250 (2.50 SD).
 */
export function sdIndexHundredths(point: QcPoint): number {
  assertPoint(point);
  const numerator = (point.value - point.mean) * 100n;
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const rounded = (magnitude * 2n + point.sd) / (point.sd * 2n);
  return Number(negative ? -rounded : rounded);
}

/** "+2.50", "-0.75", "0.00". */
export function formatSdIndex(hundredths: number): string {
  const sign = hundredths > 0 ? '+' : hundredths < 0 ? '-' : '';
  const magnitude = Math.abs(hundredths);
  return `${sign}${Math.floor(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
}

/**
 * The observed mean, SD and CV of a series, for display beside the chart only. Never used as
 * a target (the lab enters targets) and never used to judge a run.
 */
export function qcStatistics(values: readonly bigint[]): {
  n: number;
  mean: number | null;
  sd: number | null;
  cvPercent: number | null;
} {
  const n = values.length;
  if (n === 0) return { n, mean: null, sd: null, cvPercent: null };
  const scale = 10 ** QC_SCALE_DECIMALS;
  const sum = values.reduce((a, b) => a + b, 0n);
  const mean = Number(sum) / n / scale;
  if (n < 2) return { n, mean, sd: null, cvPercent: null };
  const squares = values.reduce((acc, v) => {
    const d = Number(v) / scale - mean;
    return acc + d * d;
  }, 0);
  const sd = Math.sqrt(squares / (n - 1));
  return { n, mean, sd, cvPercent: mean === 0 ? null : (sd / Math.abs(mean)) * 100 };
}

// --- Holds (plan §1.1.8, Q5, Q6) ------------------------------------------------------------

/** A run as the gate sees it. */
export interface QcGateRun {
  readonly id: string;
  readonly lotId: string;
  readonly runAt: Date;
  /** When it was recorded; breaks ties between runs measured at the same time. */
  readonly recordedAt: Date;
  readonly outcome: QcOutcome;
  readonly enteredInError: boolean;
  /** An override of this run was approved by a second person. */
  readonly overrideApproved: boolean;
}

/**
 * A scope's hold: for each lot, the latest valid run; held when that run was rejected and
 * not overridden. An acceptable later run of the same lot ends the hold (plan Q6). A scope
 * with no runs is not held (plan Q4).
 */
export function qcHold(runs: readonly QcGateRun[]): { held: boolean; heldRunIds: string[] } {
  const latest = new Map<string, QcGateRun>();
  for (const run of runs) {
    if (run.enteredInError) continue;
    const held = latest.get(run.lotId);
    const later =
      held === undefined ||
      run.runAt.getTime() > held.runAt.getTime() ||
      (run.runAt.getTime() === held.runAt.getTime() &&
        run.recordedAt.getTime() > held.recordedAt.getTime());
    if (later) latest.set(run.lotId, run);
  }
  const heldRunIds = [...latest.values()]
    .filter((run) => run.outcome === 'rejected' && !run.overrideApproved)
    .map((run) => run.id)
    .sort();
  return { held: heldRunIds.length > 0, heldRunIds };
}
